import { z } from 'zod'
import { emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'

/**
 * Judge Invitation / Temporary Initial Password (Wave P/R). DM-022/023,
 * DATA-028/029. See docs/sdd/08-platform-governance-workflows.md's Judge
 * Onboarding section for the full state machine this schema backs.
 */
export const JUDGE_INVITATION_STATUS_VALUES = [
  'pending',
  'accepted',
  'cancelled',
  'expired',
] as const
export const judgeInvitationStatusSchema = z.enum(JUDGE_INVITATION_STATUS_VALUES)
export type JudgeInvitationStatus = z.infer<typeof judgeInvitationStatusSchema>

export const judgeInvitationSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  email: emailSchema,
  status: judgeInvitationStatusSchema,
  expiresAt: utcTimestampSchema,
  inviterUserId: publicIdSchema,
  acceptedUserId: publicIdSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
  acceptedAt: utcTimestampSchema.nullable(),
  cancelledAt: utcTimestampSchema.nullable(),
  /** Set when the invited person declined (the invitation is then `cancelled`). */
  declinedAt: utcTimestampSchema.nullable(),
  declineReason: z.string().nullable(),
})
export type JudgeInvitation = z.infer<typeof judgeInvitationSchema>

/**
 * UX-025: what an Admin is allowed to see. No password, no token, no hash —
 * only these fields, ever.
 */
export const judgeInvitationDtoSchema = judgeInvitationSchema
export type JudgeInvitationDto = z.infer<typeof judgeInvitationDtoSchema>

/**
 * UX-024: Admin supplies only the email — the branching between "existing
 * account" and "new account, send Temporary Initial Password" is entirely
 * server-side (API-047).
 */
export const inviteJudgeRequestSchema = z.strictObject({
  email: emailSchema,
})
export type InviteJudgeRequest = z.infer<typeof inviteJudgeRequestSchema>

/** Existing-account branch (BR-081/API-053): accept binds invitation + caller identity + competition + role. */
export const acceptJudgeInvitationRequestSchema = z.strictObject({
  token: z.string().min(1).max(512),
})
export type AcceptJudgeInvitationRequest = z.infer<typeof acceptJudgeInvitationRequestSchema>

/** Deliberately accepts a token only and returns a tiny, verified preview — same shape as team-invite preview. */
export const judgeInvitationPreviewDtoSchema = z.object({
  valid: z.boolean(),
  competitionName: z.string().nullable(),
  email: emailSchema.nullable(),
})
export type JudgeInvitationPreviewDto = z.infer<typeof judgeInvitationPreviewDtoSchema>

/** What an invited person sees after signing in: enough to decide, never a token or the inviter's identity. */
export const myJudgeInvitationDtoSchema = z.strictObject({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  competitionName: z.string(),
  expiresAt: utcTimestampSchema,
})
export type MyJudgeInvitationDto = z.infer<typeof myJudgeInvitationDtoSchema>

export const declineJudgeInvitationRequestSchema = z.strictObject({
  reason: z.string().trim().max(1000).optional(),
})
export type DeclineJudgeInvitationRequest = z.infer<typeof declineJudgeInvitationRequestSchema>
